import type { Router } from 'vue-router'
import { startAuthorization } from '@/api/oauth'
import { useAppStore } from '@/stores/app'
import { useMenuStore } from '@/stores/menu'
import { usePermStore } from '@/stores/perm'
import { useUserStore } from '@/stores/user'
import { addMenuRoutes, flattenMenuPaths } from '@/utils/route'
import { tokenState } from '@/utils/token'

/**
 * 路由守卫（附录C §2）：
 * 1. 无 access_token 时先静默续期（Cookie 里的 refresh 仍有效就当没断过），续不上才跳 /login；
 * 2. 进入管理后台前确保该应用的菜单与权限已加载；
 * 3. 越权兜底：不属于任何菜单分支的路径按 403 处理（服务端仍独立鉴权，前端展示不构成安全边界）。
 */

/**
 * 详情页（/admin/apps/3、/admin/users/7 …）带动态参数，天然不会出现在菜单树里，
 * 但它们是列表菜单的下钻路径，因此命中「菜单路径本身」或「其任意子路径」即放行。
 */
function isMenuPathAllowed(allowed: string[], target: string): boolean {
  return allowed.some((menuPath) => target === menuPath || target.startsWith(`${menuPath}/`))
}

/** 路径结构与菜单不一致的详情页（如 rbac/roles/:roleId）用 meta.menuPath 声明归属菜单。 */
function resolveMenuPath(raw: unknown, clientId: string): string | null {
  if (typeof raw !== 'string' || raw === '') {
    return null
  }
  const resolved = raw.replace('{clientId}', clientId)
  return resolved.startsWith('/') ? resolved : `/admin/${resolved}`
}

export function registerGuards(router: Router): void {
  router.beforeEach(async (to) => {
    if (to.meta.public) {
      return true
    }

    if (tokenState.accessToken.value === null) {
      // access 只存内存，刷新页面必然丢失：先用 uc_rt 换一张，换不到才是真的没登录
      const resumed = await useUserStore().bootstrap()
      if (!resumed) {
        // 以「应用」身份发起授权码 + PKCE（附录C §2 / 附录D）：后台 uc-admin、个人中心 uc-me。
        // 不再直接跳 /login 走中心自有登录接口 —— 那条路签出的令牌没有客户端上下文，
        // 控制台只能从应用列表里兜底挑应用，菜单与按钮权限因此晚一拍（甚至判成无权限）。
        // 走协议端点后令牌自带 client_id，/callback 换到的令牌首屏即可确定应用。
        await startAuthorization(to.fullPath, to.path.startsWith('/me') ? 'me' : 'admin')
        return false // 整页跳转已发起，本次路由导航取消
      }
    }

    // 个人中心不依赖应用上下文与菜单树（附录C §4：只调 /api/me/v1/*）
    if (to.path.startsWith('/me')) {
      return true
    }

    const appStore = useAppStore()
    // 刷新后令牌（含 client_id）是静默续期时才回来的；appStore 若在续期之前就被创建过，
    // consoleClientId 会停在 null。这里补一次，否则下面的菜单/权限加载会被整段跳过，
    // 页面就会在"权限码还是空的"状态下渲染 —— v-perm 一旦在挂载时移除按钮，事后补拉也恢复不了。
    if (!appStore.consoleClientId) {
      await appStore.loadApps()
    }
    const consoleClientId = appStore.consoleClientId
    if (!consoleClientId) {
      return true // 仍然拿不到：菜单与权限由 AdminLayout 加载后自行生效（那里同样会等就绪再渲染）
    }

    const menuStore = useMenuStore()
    if (menuStore.loadedClientId !== consoleClientId) {
      // 菜单树是控制台自身的（stores/app.ts），切换"管理对象"不重拉
      await menuStore.load(consoleClientId)
    }
    if (menuStore.tree.length > 0) {
      addMenuRoutes(router, menuStore.tree)
    }

    const permStore = usePermStore()
    if (permStore.loadedClientId !== consoleClientId) {
      // 与菜单同为自己加载并记住所属应用：换应用必须重拉取，失败时 store 内部已清空权限码
      await permStore.load(consoleClientId)
    }

    if (to.path.startsWith('/admin') && to.path !== '/admin' && menuStore.tree.length > 0) {
      // 菜单树属于控制台，但 {clientId} 占位符要按"正在访问的那个应用"解析（与渲染一致）：
      // 访问 /admin/rbac/apps/<clientId>/menus 时若拿控制台应用去解析，会被误判成越权
      const param = Array.isArray(to.params.clientId) ? to.params.clientId[0] : to.params.clientId
      const clientId = String(param ?? appStore.currentClientId ?? '')
      const allowed = flattenMenuPaths(menuStore.tree, clientId)
      // 详情页带动态参数，不在菜单树里：按「菜单路径 + 其子路径」放行；
      // 路径结构与菜单不一致的（rbac/roles/:roleId）由 meta.menuPath 声明归属菜单
      const declared = resolveMenuPath(to.meta.menuPath, clientId)
      const targets = declared === null ? [to.path] : [to.path, declared]
      if (!targets.some((target) => isMenuPathAllowed(allowed, target))) {
        return { name: 'forbidden' }
      }
    }

    return true
  })
}
